/**
 * DataTable.tsx - Universal data table component with AG Grid
 * 
 * CHANGE HISTORY:
 * - 251101_0725 [pending] danielbeer: Refactored to use BaseGridView (AG Grid) for better performance and reliability
 * - 251031_2000 [pending] danielbeer: Added multi-select and context menu features (TanStack version)
 * - 251031_1900 [pending] danielbeer: Initial creation with TanStack Table
 */
import React, { useState, useMemo, useCallback, useRef } from 'react';
import BaseGridView from '../BaseGridView/BaseGridView';
import { useContextMenu } from '../components/ContextMenu/useContextMenu';
import ContextMenu from '../components/ContextMenu/ContextMenu';
import './DataTable.css';

export interface DataTableColumn<T = any> {
  field: string;
  headerName: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  flex?: number;
  sortable?: boolean;
  filter?: boolean | string;
  resizable?: boolean;
  cellRenderer?: string | ((params: any) => any);
  valueGetter?: (params: any) => any;
  hide?: boolean;
}

export interface DataTableProps<T extends Record<string, any>> {
  // Data
  data: T[];
  columns: DataTableColumn<T>[];
  
  // Features
  filtering?: boolean;
  sorting?: boolean;
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  columnResizing?: boolean;
  columnReordering?: boolean;
  columnVisibility?: boolean;
  
  // Selection
  enableMultiSelect?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  
  // Context Menu
  enableContextMenu?: boolean;
  onCopyColumn?: (columnId: string, data: any[]) => void;
  onCopyCell?: (value: any) => void;
  onCopyRow?: (row: T) => void;
  
  // Callbacks
  onRowClick?: (row: T) => void;
  
  // Styling
  className?: string;
  height?: string;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  
  // Loading state
  loading?: boolean;
  loadingMessage?: string;
  
  // Empty state
  emptyMessage?: string;
}

export const DataTable = <T extends Record<string, any>>({
  data,
  columns,
  filtering = true,
  sorting = true,
  pagination = true,
  pageSize = 50,
  pageSizeOptions = [25, 50, 100, 200],
  columnResizing = true,
  columnReordering = true,
  columnVisibility = true,
  enableMultiSelect = false,
  onSelectionChange,
  enableContextMenu = false,
  onCopyColumn,
  onCopyCell,
  onCopyRow,
  onRowClick,
  className = '',
  height = '600px',
  striped = false,
  hoverable = true,
  compact = false,
  loading = false,
  loadingMessage = 'Loading...',
  emptyMessage = 'No data available',
}: DataTableProps<T>) => {
  const gridRef = useRef<any>(null);
  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  
  // Context menu
  const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();
  
  // Convert columns to AG Grid format
  const agColumns = useMemo(() => {
    const cols = columns.map(col => ({
      field: col.field,
      headerName: col.headerName,
      width: col.width,
      minWidth: col.minWidth || 100,
      maxWidth: col.maxWidth,
      flex: col.flex,
      sortable: col.sortable !== false && sorting,
      filter: col.filter !== false && filtering,
      resizable: col.resizable !== false && columnResizing,
      cellRenderer: col.cellRenderer,
      valueGetter: col.valueGetter,
      hide: col.hide,
      // Enable column menu for visibility toggle (v34 uses suppressHeaderMenuButton)
      suppressHeaderMenuButton: !columnVisibility,
    }));
    
    // Checkbox column handled by rowSelection.checkboxes in v34
    
    return cols;
  }, [columns, sorting, filtering, columnResizing, columnVisibility, enableMultiSelect]);
  
  // Handle selection change
  const handleSelectionChange = useCallback((rows: T[]) => {
    setSelectedRows(rows);
    if (onSelectionChange) {
      onSelectionChange(rows);
    }
  }, [onSelectionChange]);
  
  // Handle row click
  const handleRowClick = useCallback((params: any) => {
    if (onRowClick) {
      onRowClick(params.data);
    }
  }, [onRowClick]);
  
  // Handle cell context menu
  const handleCellContextMenu = useCallback((params: any) => {
    if (!enableContextMenu) return;
    
    params.event.preventDefault();
    
    openContextMenu(params.event, {
      type: 'cell',
      row: params.data,
      columnId: params.column.getColId(),
      value: params.value,
    });
  }, [enableContextMenu, openContextMenu]);
  
  // Handle header context menu
  const handleHeaderContextMenu = useCallback((params: any) => {
    if (!enableContextMenu) return;
    
    params.event.preventDefault();
    
    openContextMenu(params.event, {
      type: 'column',
      columnId: params.column.getColId(),
    });
  }, [enableContextMenu, openContextMenu]);
  
  // Context menu items
  const contextMenuItems = useMemo(() => {
    if (!contextMenu.data) return [];
    
    if (contextMenu.data.type === 'column') {
      return [
        {
          id: 'copy-column',
          label: 'Copy Column',
          icon: '📋',
          action: () => {
            if (onCopyColumn && gridRef.current) {
              const columnData = data.map(row => row[contextMenu.data.columnId]);
              onCopyColumn(contextMenu.data.columnId, columnData);
            }
            closeContextMenu();
          },
        },
        { separator: true },
        {
          id: 'hide-column',
          label: 'Hide Column',
          icon: '👁️',
          action: () => {
            if (gridRef.current?.api) {
              gridRef.current.api.setColumnsVisible([contextMenu.data.columnId], false);
            }
            closeContextMenu();
          },
        },
      ];
    } else if (contextMenu.data.type === 'cell') {
      return [
        {
          id: 'copy-cell',
          label: 'Copy Cell',
          icon: '📋',
          action: () => {
            if (onCopyCell) {
              onCopyCell(contextMenu.data.value);
            }
            closeContextMenu();
          },
        },
        {
          id: 'copy-row',
          label: 'Copy Row',
          icon: '📄',
          action: () => {
            if (onCopyRow) {
              onCopyRow(contextMenu.data.row);
            }
            closeContextMenu();
          },
        },
        { separator: true },
        {
          id: 'select-row',
          label: 'Select Row',
          icon: '✓',
          action: () => {
            if (gridRef.current?.api) {
              const rowNode = gridRef.current.api.getRowNode(contextMenu.data.row.id);
              if (rowNode) {
                rowNode.setSelected(true);
              }
            }
            closeContextMenu();
          },
        },
      ];
    }
    
    return [];
  }, [contextMenu.data, data, onCopyColumn, onCopyCell, onCopyRow, closeContextMenu]);
  
  // Row selection config (memoized to prevent infinite re-renders)
  const rowSelectionConfig = useMemo(() => {
    if (enableMultiSelect) {
      return {
        mode: 'multiRow' as const,
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: true, // Allow clicking checkboxes
        enableSelectionWithoutKeys: true // Allow Cmd/Shift+Click on checkboxes
      };
    }
    return {
      mode: 'singleRow' as const,
      checkboxes: false,
      enableClickSelection: true
    };
  }, [enableMultiSelect]);

  // Grid options
  const gridOptions = useMemo(() => ({
    animateRows: true,
    // Use 'name' field as row ID if available, otherwise use index
    getRowId: (params: any) => params.data.name || params.data.id || String(params.node.rowIndex),
    // Context menu
    suppressContextMenu: !enableContextMenu,
  }), [enableContextMenu]);
  
  const containerClass = `data-table ${className} ${striped ? 'data-table-striped' : ''} ${hoverable ? 'data-table-hoverable' : ''} ${compact ? 'data-table-compact' : ''}`;
  
  return (
    <div className={containerClass}>
      {/* Selection Info Banner */}
      {enableMultiSelect && selectedRows.length > 0 && (
        <div className="data-table-selection-info">
          <span>{selectedRows.length} row(s) selected</span>
          <button onClick={() => {
            if (gridRef.current?.api) {
              gridRef.current.api.deselectAll();
            }
          }}>
            Clear Selection
          </button>
        </div>
      )}
      
      {/* Grid */}
      <BaseGridView
        data={data}
        columns={agColumns}
        pagination={pagination}
        pageSize={pageSize}
        filtering={filtering}
        sorting={sorting}
        editable={false}
        rowSelection={rowSelectionConfig}
        onSelectionChange={handleSelectionChange}
        onRowClick={handleRowClick}
        height={height}
        onCellContextMenu={enableContextMenu ? handleCellContextMenu : undefined}
        gridOptions={{
          ...gridOptions,
          onGridReady: (params: any) => {
            gridRef.current = params;
          },
        }}
      />
      
      {/* Context Menu */}
      {enableContextMenu && (
        <ContextMenu
          isOpen={contextMenu.isOpen}
          items={contextMenuItems}
          position={contextMenu.position}
          onClose={closeContextMenu}
        />
      )}
    </div>
  );
};

export default DataTable;
